import { ehQuantidadeZero, hasValue } from "./data";
import { F } from "./gerencialFields";
import { toDate } from "./status";

export const TERCEIROS_ITEMS = [
  {
    id: "serralheria",
    field: F.SERRALHERIA_TERCEIROS,
    label: "Serralheria",
    quantidadeField: F.SERRALHERIA_QTD,
    descritivoField: F.SERRALHERIA_DESCRITIVO,
  },
  {
    id: "perfil-aluminio",
    field: F.PERFIL_ALUMINIO_TERCEIROS,
    label: "Perfil de Alumínio",
    quantidadeField: F.PERFIL_ALUMINIO_QTD,
    descritivoField: F.PERFIL_ALUMINIO_DESCRITIVO,
  },
  {
    id: "portas-frentes",
    field: F.PORTAS_FRENTES_TERCEIROS,
    label: "Portas / Frentes",
    quantidadeField: F.PORTAS_FRENTES_QTD,
    descritivoField: F.PORTAS_FRENTES_DESCRITIVO,
  },
  {
    id: "vidracaria",
    field: F.VIDRACARIA,
    label: "Vidraçaria",
    quantidadeField: F.VIDRACARIA_QTD,
    descritivoField: F.VIDRACARIA_DESCRITIVO,
  },
  {
    id: "tapecaria",
    field: F.TAPECARIA_TERCEIROS,
    label: "Tapeçaria",
    quantidadeField: F.TAPECARIA_QTD,
    descritivoField: F.TAPECARIA_DESCRITIVO,
  },
  {
    id: "ferragens-especiais",
    field: F.FERRAGENS_ESPECIAIS_TERCEIROS,
    label: "Ferragens Especiais",
    quantidadeField: F.FERRAGENS_ESPECIAIS_QTD,
    descritivoField: F.FERRAGENS_ESPECIAIS_DESCRITIVO,
  },
];

// Verniz (com enfolhamento) e terceirizado, mas fica fora do bloco BI-BN:
// o status e a coluna CB (formula =AJ, sobrescrita com a data ao receber) e o
// RECEBIMENTO TERCEIROS (BO) nao o cobre — so a data em CB recebe.
export const VERNIZ_TERCEIRIZADO = {
  id: "verniz",
  field: F.VERNIZ_ACAB,
  label: "Verniz",
  quantidadeField: F.VERNIZ_QTD,
  descritivoField: F.ACABAMENTO_DESCRITIVO,
  recebidoPorRecebimentoTerceiros: false,
};

// Tudo o que a tela Terceiros acompanha. TERCEIROS_ITEMS continua sendo so o
// bloco BI-BN (Entregas usa essa lista junto com o RECEBIMENTO TERCEIROS).
export const ITENS_FILA_TERCEIROS = [...TERCEIROS_ITEMS, VERNIZ_TERCEIRIZADO];

function isItemMarcadoX(value) {
  if (!hasValue(value)) return false;
  return String(value).trim().toUpperCase() === "X";
}

// C-019/H-069: detecta strings ISO YYYY-MM-DD (resultado de Date->string)
// e numeros que sejam serial-date do Excel (>= 25569 ~ 1970-01-01).
// Quando a celula esperada nao for descritivo nem quantidade, esses valores
// sao DATAS travestidas — devem ser ignorados na coluna "Quantidade".
//
// 2026-05-28 (Fix #3): estendido para reconhecer mais formatos. A usuaria
// reportou que preenchendo DATA no formato BR (DD/MM/YYYY) na coluna
// principal do item, ele continuava aparecendo como pendente — porque o
// pareceData() antigo so reconhecia ISO. Agora cobre:
//   - String ISO: "2026-05-27"
//   - String BR : "27/05/2026" ou "27/5/26"
//   - Numero Excel serial: 25_000 < n < 80_000
//   - Objeto Date
function pareceData(value) {
  if (value == null) return false;
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return false;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return true; // ISO (com ou sem hora)
    if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s)) return true; // BR
    return false;
  }
  if (typeof value === "number") {
    return value > 25_000 && value < 80_000;
  }
  return false;
}

// "0" = zero unidades: o ambiente nao leva esse item. Mesma leitura de
// isXOuVazio em toc/etapas — sem ela um "0" contava como item pendente.
export function ambientePrecisaItem(amb, item) {
  const value = amb?.[item.field];
  if (!hasValue(value)) return false;
  if (isItemMarcadoX(value)) return false;
  // 2026-09-23: zero da fórmula espelho = nenhum item (ver ehQuantidadeZero).
  if (ehQuantidadeZero(value)) return false;
  // 2026-05-28 (Fix #3): data preenchida na coluna principal do item
  // (ex.: "27/05/2026" em PORTAS FRENTES TERCEIROS) significa que o item
  // foi finalizado/recebido — equivalente a um "X" mais informativo.
  // Antes esse valor caia no return true abaixo e o item continuava
  // aparecendo como pendente eternamente. Agora trata data como concluido.
  if (pareceData(value)) return false;
  // Tambem considera "precisa" quando o valor e um numero (>0) ou texto
  // descritivo. Datas em coluna de quantidade nao significam pendencia
  // — significam apontamento de envio (ja registrado em outra coluna).
  return true;
}

function valorUtilParaDescricao(valor) {
  if (!hasValue(valor)) return null;
  if (pareceData(valor)) return null;
  const s = String(valor).trim();
  // Filtra marcador de status "X" — ele indica entrega/conclusao em
  // colunas auxiliares, nao e quantidade nem descritivo.
  if (s.toUpperCase() === "X") return null;
  return s || null;
}

export function descricaoItemPendente(amb, item) {
  if (item.descritivoField || item.quantidadeField) {
    const qtd = valorUtilParaDescricao(
      item.quantidadeField ? amb?.[item.quantidadeField] : null
    );
    const desc = valorUtilParaDescricao(
      item.descritivoField ? amb?.[item.descritivoField] : null
    );
    if (qtd && desc) return `${qtd}× ${desc}`;
    if (desc) return desc;
    if (qtd) return qtd;
  }

  // Fallback: usar o valor da coluna principal (somente texto util).
  // Datas aqui sao apontamentos de envio para terceiros — exibir mensagem
  // padrao em vez de "2025-12-05".
  const valor = amb?.[item.field];
  if (!hasValue(valor)) return "Pendente";
  if (pareceData(valor)) {
    return "Item enviado, sem descrição detalhada";
  }
  if (typeof valor === "number") {
    return String(valor);
  }
  return String(valor).trim() || "Pendente";
}

// Na planilha, o pedido ao terceiro corre EM PARALELO a producao: a coluna
// BP (ENTREGA TERCEIROS) e WORKDAY(OTIMIZACAO DO LOTE, prazo). Por isso a
// fila de terceiros comeca na otimizacao do lote (ou na aprovacao, se o lote
// ainda nao foi otimizado) — e nao na saida de Maquinas, que escondia itens
// de ambientes ainda em Compras/Maquinas.
export function dataEntradaTerceiros(amb) {
  return toDate(amb?.[F.OTIMIZACAO_DO_LOTE]) || toDate(amb?.[F.APROVACAO_DO_EXECUTIVO]);
}

export function ambienteLiberadoParaTerceiros(amb) {
  return Boolean(dataEntradaTerceiros(amb));
}

// Prazo do TERCEIRO (BP), nao a entrega ao cliente (N). ENTREGA PREVISTA so
// entra quando a planilha ainda nao calculou BP.
export function prazoTerceiros(amb) {
  return toDate(amb?.[F.ENTREGA_TERCEIROS]) || toDate(amb?.[F.ENTREGA_PREVISTA]);
}

// Ambiente ja carregado com item de terceiro em aberto: o item segue direto
// para a obra (vidro, espelho, serralheria instalados no local). Carregar o
// ambiente nao recebe o item — so a data na coluna do item (ou em
// RECEBIMENTO TERCEIROS) recebe.
export function dataCarregamento(amb) {
  return toDate(amb?.[F.CARREGAMENTO]);
}

const EXCEL_EPOCH_UTC_MS = Date.UTC(1899, 11, 30);
const MS_POR_DIA = 86_400_000;

// Data digitada na coluna do item (BI-BN) = item recebido nessa data.
export function dataRecebimentoItem(amb, item) {
  const value = amb?.[item.field];
  if (!pareceData(value)) return null;
  if (typeof value === "number") {
    // Serial do Excel (dias desde 30/12/1899) — toDate() leria como epoch ms.
    const utc = new Date(EXCEL_EPOCH_UTC_MS + value * MS_POR_DIA);
    return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
  }
  return toDate(value);
}

export function temItensTerceirosPendentes(amb) {
  return ambienteLiberadoParaTerceiros(amb) && listarItensPendentes(amb).length > 0;
}

export function listarItensPendentes(amb) {
  const recebimento = toDate(amb?.[F.RECEBIMENTO_TERCEIROS]);
  return ITENS_FILA_TERCEIROS.filter((item) => {
    if (recebimento && item.recebidoPorRecebimentoTerceiros !== false) return false;
    return ambientePrecisaItem(amb, item);
  }).map(
    (item) => ({
      ...item,
      descricao: descricaoItemPendente(amb, item),
    })
  );
}
